import { Button, useRefAutoUpdate, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useEffect, useState } from "react";
import { MasterUI } from "../common/MasterUI";
import { AnimatedBackground } from "./AnimatedBackground";
import { AppLoadingView } from "./AppLoadingView";
import styles from "./ConnexionView.module.css";

export interface ConnexionViewProps extends WithNativeHTMLProps<HTMLDivElement> {
  status: "connecting" | "connected" | "disconnected";
  hide?: boolean;
  retry?: () => void;
  retryAuto?: boolean;
  retryAfter?: number; // seconds
  className?: string;
  style?: React.CSSProperties;
}

export interface ConnexionViewRef {}

const ConnexionView = React.forwardRef(
  (
    { status, hide = false, retry, retryAuto = true, retryAfter = 15 }: ConnexionViewProps,
    _forwardRef: ForwardedRef<ConnexionViewRef | undefined>,
  ) => {
    const [counterRetry, setCounterRetry] = useState<number>(-1);
    // Tracks whether the websocket has ever reached "connected" since this
    // component mounted. Until then, transient "disconnected" reports from
    // the socket layer (typical at login while the connection is still
    // being negotiated) are treated as "connecting" so the user doesn't see
    // a flash of the error UI before the first successful handshake.
    const [hasConnected, setHasConnected] = useState<boolean>(false);
    const retryAutoRef = useRefAutoUpdate(retryAuto);
    const retryAfterRef = useRefAutoUpdate(retryAfter);
    const retryRef = useRefAutoUpdate(retry);

    const retryConnectSocket = () => {
      if (!retryRef.current) return;
      setCounterRetry(-1);
      retryRef.current();
    };

    useEffect(() => {
      if (status === "connected") {
        setHasConnected(true);
      } else if (status === "disconnected") {
        if (retryAutoRef.current) setCounterRetry(retryAfterRef.current);
      }
    }, [status]);

    useEffect(() => {
      if (status === "disconnected") {
        if (counterRetry > 0) {
          const timeout = setTimeout(() => {
            setCounterRetry(counterRetry - 1);
          }, 1000);
          return () => clearTimeout(timeout);
        } else if (counterRetry === 0) {
          retryConnectSocket();
        }
      }
    }, [counterRetry, status]);

    if (status === "connected" || hide) return null;

    // Treat the first run of "disconnected" as "connecting" — see comment on
    // hasConnected above.
    const effectiveStatus = !hasConnected && status === "disconnected" ? "connecting" : status;

    if (effectiveStatus === "connecting") {
      return <AppLoadingView message="Connexion en cours" />;
    }

    return (
      <MasterUI>
        <AnimatedBackground contentClassName={styles.message}>
          <div className={styles.errorDescription}>{"Une erreur est survenue durant la connexion.\nVérifiez votre connexion internet."}</div>
          {counterRetry > 0 ? <div className={styles.errorRetry}>{`Reconnexion dans ${counterRetry} secondes...`}</div> : null}
          <Button className={styles.retryButton} title={counterRetry > 0 ? "Réessayer maintenant" : "Réessayer"} onPressOut={() => retryConnectSocket()} />
        </AnimatedBackground>
      </MasterUI>
    );
  },
);

export default ConnexionView;
